import { useAuth } from "react-oidc-context";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "../api";

interface HealthResponse {
  status: string;
  email?: string;
  sub?: string;
}

interface StatsResponse {
  counts: Record<string, number>;
  total: number;
  top: TopJob[];
}

interface TopJob {
  job_id: string;
  title: string;
  company: string;
  score?: number;
  status: string;
}

const STATUS_ORDER = ["new", "fetched", "scored", "prepared", "applied", "skipped", "fetch_failed"];

export function Dashboard() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const qc = useQueryClient();

  const health = useQuery({
    queryKey: ["health"],
    queryFn: () => api<HealthResponse>("/health", token),
    enabled: !!token,
  });

  const stats = useQuery({
    queryKey: ["stats"],
    queryFn: () => api<StatsResponse>("/jobs/stats", token),
    enabled: !!token,
    refetchInterval: 10000,
  });

  const startRun = useMutation({
    mutationFn: () => api<{ name: string }>("/runs", token, { method: "POST", body: JSON.stringify({}) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["runs"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-50">Dashboard</h1>
          <p className="mt-1 text-sm text-slate-400">
            Signed in as {health.data?.email ?? "…"}
          </p>
        </div>
        <button
          type="button"
          className="rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-accent-fg transition hover:opacity-90 disabled:opacity-50"
          onClick={() => startRun.mutate()}
          disabled={startRun.isPending}
        >
          {startRun.isPending ? "Starting…" : "Run pipeline now"}
        </button>
      </header>

      {startRun.error && (
        <div className="rounded-md border border-rose-700 bg-rose-950/40 px-4 py-2 text-sm text-rose-200">
          {(startRun.error as Error).message}
        </div>
      )}

      <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Tile label="Total jobs" value={stats.data?.total ?? 0} link="/inbox" />
        {STATUS_ORDER.map((s) => (
          <Tile
            key={s}
            label={s.replace("_", " ")}
            value={stats.data?.counts[s] ?? 0}
            link={`/inbox?status=${s}`}
            tone={
              s === "prepared"
                ? "emerald"
                : s === "fetch_failed"
                ? "rose"
                : s === "scored"
                ? "indigo"
                : "neutral"
            }
          />
        ))}
      </section>

      <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
          Top fits
        </h2>
        {!stats.data?.top.length ? (
          <p className="mt-3 text-sm text-slate-500">No scored jobs yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-800">
            {stats.data.top.map((j) => (
              <li key={j.job_id} className="flex items-center justify-between py-2">
                <Link
                  to={`/jobs/${j.job_id}`}
                  className="flex-1 truncate text-sm text-slate-100 hover:text-accent"
                >
                  <span className="font-medium">{j.title}</span>
                  <span className="ml-2 text-slate-400">{j.company}</span>
                </Link>
                <span
                  className={`ml-3 inline-flex h-7 min-w-[2.5rem] items-center justify-center rounded-full px-2 text-xs font-medium ${
                    (j.score ?? 0) >= 75
                      ? "bg-emerald-900/40 text-emerald-200"
                      : (j.score ?? 0) >= 50
                      ? "bg-amber-900/40 text-amber-200"
                      : "bg-slate-800 text-slate-400"
                  }`}
                >
                  {j.score ?? "—"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Tile({
  label,
  value,
  link,
  tone = "neutral",
}: {
  label: string;
  value: number;
  link?: string;
  tone?: "neutral" | "emerald" | "rose" | "indigo";
}) {
  const toneClass =
    tone === "emerald"
      ? "border-emerald-800 bg-emerald-950/30"
      : tone === "rose"
      ? "border-rose-800 bg-rose-950/30"
      : tone === "indigo"
      ? "border-indigo-800 bg-indigo-950/30"
      : "border-slate-800 bg-slate-900";
  const inner = (
    <div className={`rounded-xl border ${toneClass} p-4`}>
      <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-slate-50">{value}</div>
    </div>
  );
  return link ? <Link to={link}>{inner}</Link> : inner;
}
